import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Icon,
  TextField,
  Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { backendUrl } from "../../config/api";
import { theme } from "../../config/theme";
import CoreContainedButton from "../../core/inputs/CoreContainedButton";
import { getApiHeader } from "../../helper/apiActionHelper";

function FileDropZone({ label, file, onFileChange }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) validateAndSet(dropped);
  };

  const handleChange = (e) => {
    const selected = e.target.files[0];
    if (selected) validateAndSet(selected);
  };

  const validateAndSet = (f) => {
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) {
      alert("Only .xlsx and .xls files are supported.");
      return;
    }
    onFileChange(f);
  };

  return (
    <Box
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      sx={{
        border: `2px dashed ${dragging ? theme.palette.primary.main : "#bbb"}`,
        borderRadius: 2,
        p: 3,
        textAlign: "center",
        cursor: "pointer",
        bgcolor: dragging ? "rgba(161,79,179,0.05)" : "#fafafa",
        transition: "all 0.2s",
        minHeight: 120,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        onChange={handleChange}
      />
      <Icon
        sx={{ fontSize: 36, color: file ? theme.palette.primary.main : "#bbb" }}
      >
        {file ? "check_circle" : "upload_file"}
      </Icon>
      <Typography
        variant="body2"
        color={file ? "textPrimary" : "textSecondary"}
        fontWeight={file ? 600 : 400}
      >
        {file ? file.name : label}
      </Typography>
      {!file && (
        <Typography variant="caption" color="textSecondary">
          Drag & drop or click to browse (.xlsx, .xls)
        </Typography>
      )}
      {file && (
        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: "pointer", textDecoration: "underline" }}
          onClick={(e) => {
            e.stopPropagation();
            onFileChange(null);
          }}
        >
          Remove
        </Typography>
      )}
    </Box>
  );
}

export default function MergeExcel() {
  const accessToken = useSelector((state) => state?.auth?.accessToken);

  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [primaryColumn, setPrimaryColumn] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = file1 && file2 && primaryColumn.trim() && !loading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError("");
    setLoading(true);

    const formData = new FormData();
    formData.append("fileWrong", file1);
    formData.append("fileRight", file2);
    formData.append("primaryColumn", primaryColumn.trim());

    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await fetch(`${backendUrl}/merge/excels`, {
        method: "POST",
        headers,
        body: formData,
      });

      if (response.status === 200) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "merged_output.xlsx";
        link.click();
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
        setFile1(null);
        setFile2(null);
        setPrimaryColumn("");
      } else {
        let msg = "Merge failed. Please try again.";
        try {
          const data = await response.json();
          msg = data?.message || msg;
        } catch (_) {}
        setError(msg);
      }
    } catch (err) {
      setError(err?.message || "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 700,
        mx: "auto",
        mt: 4,
        px: 2,
        padding: 4,
        bgcolor: "#fff",
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Merge Excel Files
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={3}>
        Upload two Excel files and specify the common column to merge on. The
        merged result will be downloaded automatically.
      </Typography>

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" mb={1} fontWeight={600}>
            Large wrong file
          </Typography>
          <FileDropZone
            label="Upload file"
            file={file1}
            onFileChange={setFile1}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" mb={1} fontWeight={600}>
            Small correct file
          </Typography>
          <FileDropZone
            label="Upload file"
            file={file2}
            onFileChange={setFile2}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" mb={1} fontWeight={600}>
            Primary Column Name
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="e.g. employee_id, order_no"
            value={primaryColumn}
            onChange={(e) => setPrimaryColumn(e.target.value)}
            helperText="The column name that exists in both files and will be used as the merge key."
          />
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Box
              sx={{
                bgcolor: "#fdecea",
                border: "1px solid #f44336",
                borderRadius: 1,
                px: 2,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ color: "#f44336", fontSize: 20 }}>error</Icon>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Box>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end">
            <CoreContainedButton
              disabled={!canSubmit}
              onClick={handleSubmit}
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Icon>merge_type</Icon>
                )
              }
            >
              {loading ? "Merging…" : "Merge & Download"}
            </CoreContainedButton>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
